import React, { useState, useEffect, useMemo } from 'react';
import { notificationAPI } from '../../lib/api';
import { formatDateTime } from '../../lib/utils';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Bell, Check, CheckCheck, Loader2, Trash2, UserPlus, Crown,
  HeadphonesIcon, Star, AlertCircle, CreditCard, RefreshCw, Clock
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_TYPES = {
  signups: ['landlord_registration'],
  subscriptions: ['subscription_purchase', 'subscription_renewal', 'subscription_refund', 'subscription_cancelled', 'landlord_trial_expired', 'landlord_trial_ending', 'landlord_subscription_expiring'],
  support: ['new_support_ticket', 'support_ticket_reply'],
};

const getCategory = (type) => {
  for (const [cat, types] of Object.entries(CATEGORY_TYPES)) {
    if (types.includes(type)) return cat;
  }
  return 'other';
};

const getIcon = (type) => {
  switch (type) {
    case 'landlord_registration':
      return <UserPlus className="w-5 h-5 text-emerald-500" />;
    case 'subscription_purchase':
      return <Crown className="w-5 h-5 text-amber-500" />;
    case 'subscription_renewal':
      return <Crown className="w-5 h-5 text-amber-500" />;
    case 'subscription_refund':
      return <RefreshCw className="w-5 h-5 text-red-500" />;
    case 'subscription_cancelled':
      return <Clock className="w-5 h-5 text-red-500" />;
    case 'landlord_trial_expired':
      return <Clock className="w-5 h-5 text-orange-500" />;
    case 'landlord_trial_ending':
      return <Clock className="w-5 h-5 text-amber-500" />;
    case 'new_support_ticket':
    case 'support_ticket_reply':
      return <HeadphonesIcon className="w-5 h-5 text-blue-500" />;
    case 'rating':
      return <Star className="w-5 h-5 text-yellow-500" />;
    case 'payment':
      return <CreditCard className="w-5 h-5 text-green-500" />;
    default:
      return <AlertCircle className="w-5 h-5 text-muted-foreground" />;
  }
};

const CATEGORY_BADGE = {
  signups: { label: 'Landlord Signup', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400' },
  subscriptions: { label: 'Subscription', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400' },
  support: { label: 'Support', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-400' },
  other: { label: 'Platform', cls: 'bg-muted text-muted-foreground' },
};

export default function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('all');

  const syncBell = () => window.dispatchEvent(new Event('notifications-updated'));

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await notificationAPI.getAll();
      setNotifications(res.data);
    } catch (e) {
      console.error('Error loading notifications:', e);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const counts = useMemo(() => {
    const c = { all: notifications.length, signups: 0, subscriptions: 0, support: 0, other: 0 };
    notifications.forEach((n) => { c[getCategory(n.type)] += 1; });
    return c;
  }, [notifications]);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.read).length, [notifications]);

  const filtered = useMemo(
    () => (filter === 'all' ? notifications : notifications.filter((n) => getCategory(n.type) === filter)),
    [notifications, filter]
  );

  const handleMarkAsRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications((prev) => prev.map((n) => (n.notification_id === id ? { ...n, read: true } : n)));
      syncBell();
    } catch (e) {
      toast.error('Failed to mark as read');
    }
  };

  const handleMarkAllRead = async () => {
    setBusy(true);
    try {
      await notificationAPI.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      syncBell();
      toast.success('All notifications marked as read');
    } catch (e) {
      toast.error('Failed to mark all as read');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await notificationAPI.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.notification_id !== id));
      syncBell();
      toast.success('Notification deleted');
    } catch (e) {
      toast.error('Failed to delete notification');
    }
  };

  const handleClearAll = async () => {
    setBusy(true);
    try {
      for (const n of filtered) {
        await notificationAPI.deleteNotification(n.notification_id);
      }
      setNotifications((prev) => prev.filter((n) => !filtered.some((f) => f.notification_id === n.notification_id)));
      syncBell();
      toast.success('Notifications cleared');
    } catch (e) {
      toast.error('Failed to clear notifications');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-notifications-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold flex items-center gap-3">
            Notifications
            {unreadCount > 0 && (
              <Badge className="bg-[#C85A42] text-white" data-testid="admin-notif-unread-badge">
                {unreadCount} unread
              </Badge>
            )}
          </h1>
          <p className="text-muted-foreground mt-1">
            Landlord signups, subscription activity, support tickets and platform alerts
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllRead}
            disabled={busy || unreadCount === 0}
            data-testid="admin-notif-mark-all-read"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-1.5" />}
            Mark All Read
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:text-destructive"
            onClick={handleClearAll}
            disabled={busy || filtered.length === 0}
            data-testid="admin-notif-clear-all"
          >
            <Trash2 className="w-4 h-4 mr-1.5" />
            Clear {filter === 'all' ? 'All' : 'Shown'}
          </Button>
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="all" data-testid="admin-notif-tab-all">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="signups" data-testid="admin-notif-tab-signups">Landlord Signups ({counts.signups})</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="admin-notif-tab-subscriptions">Subscriptions ({counts.subscriptions})</TabsTrigger>
          <TabsTrigger value="support" data-testid="admin-notif-tab-support">Support ({counts.support})</TabsTrigger>
          <TabsTrigger value="other" data-testid="admin-notif-tab-other">Other ({counts.other})</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center" data-testid="admin-notif-empty-state">
            <Bell className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="font-medium">No notifications</p>
            <p className="text-sm text-muted-foreground mt-1">
              {filter === 'all' ? "You're all caught up." : 'No notifications in this category.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((n) => {
            const cat = getCategory(n.type);
            const badge = CATEGORY_BADGE[cat];
            return (
              <Card
                key={n.notification_id}
                className={cn('transition-colors', !n.read && 'border-l-4 border-l-[#C85A42] bg-blue-50/40 dark:bg-blue-950/10')}
                data-testid={`admin-notif-item-${n.notification_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="mt-0.5 shrink-0">{getIcon(n.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {n.title && <p className={cn('text-sm', !n.read ? 'font-semibold' : 'font-medium')}>{n.title}</p>}
                        <Badge variant="secondary" className={cn('text-[10px] px-1.5 py-0', badge.cls)}>
                          {badge.label}
                        </Badge>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-[#C85A42] shrink-0" />}
                      </div>
                      <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                      <p className="text-xs text-muted-foreground mt-1.5">{formatDateTime(n.created_at)}</p>
                    </div>
                    <div className="flex items-start gap-1 shrink-0">
                      {!n.read && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          title="Mark as read"
                          onClick={() => handleMarkAsRead(n.notification_id)}
                          data-testid={`admin-notif-read-${n.notification_id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        title="Delete"
                        onClick={() => handleDelete(n.notification_id)}
                        data-testid={`admin-notif-delete-${n.notification_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
